import { getFormErrorMessage } from "@core/utility/get-error-message";
import { AgentFormRule, AgentFormSchema } from "@factory/form/form.rule";
import { useFormDefaultAgent } from "@factory/form/form-default-agent.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseAgentModel } from "@factory/model/query-model-agent";
import { 
  useQyDeleteAgent, 
  useQyGetAgentById, 
  useQyUpdateAgent} from "@factory/query/agent.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditAgent() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultAgent();
  const navigate = useNavigate();
  const { mapUpdateAgent, mapDeleteAgent } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { agentId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Agent
  const handleGetApiSuccess = (data: GetResponseAgentModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("code", responseData?.code || "");
      setValue("markupPercentage", responseData?.markup_percentage);
  };
  const {
    data: agentResponse,
    isLoading,
    isError: agentError,
  } = useQyGetAgentById(agentId || "", handleGetApiSuccess);
  const agentData = agentResponse?.data?.[0];

  // API UPDATE Agent
  const handleUpdateApiSuccess = () => {
    showSuccess("Agent updated successfully");
  };
  const { mutate: updateAgent, isLoading: isEditLoading } = useQyUpdateAgent(
    handleUpdateApiSuccess
  );

  // API DELETE Agent
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Agent deleted successfully");
  };
  const { mutate: deleteAgent } = useQyDeleteAgent(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<AgentFormSchema>({
    defaultValues: getDefault(agentData),
    resolver: zodResolver(AgentFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: AgentFormSchema) => {
    if (!agentData || !agentData.code) {
      throw new Error("No agent data");
    }

    const formData = mapUpdateAgent(form, agentData.code);
    updateAgent(formData);
  };
  const handleValidateError = (err: FieldErrors<AgentFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!agentData || !agentData.code) {
      throw new Error("No agent data found");
    }

    const form = mapDeleteAgent(agentData.code);
    deleteAgent(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    agentId,
    agentData,
    isLoading,
    agentError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}